import { FC, ReactNode } from 'react';
import styled, { CSSProperties } from 'styled-components';

import { BodilColor, NorlysColor, NorlysFont } from '~src/branding';
import { Heading } from '~src/components/Typography';

const SelectContainer = styled.div({
  position: 'relative',
  borderRadius: '20px',
  height: '38px',
  backgroundColor: '#F0F0F0',
  display: 'flex',
  flexFlow: 'row nowrap',
  justifyContent: 'space-around',
  userSelect: 'none',
});

const SelectOption = styled.div<{ selected?: boolean }>(({ selected }) => ({
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  ...NorlysFont.style('default', 14),
  cursor: selected ? 'default' : 'pointer',
  transition: 'opacity 0.25s',
}));

const Selection = styled.div<{ backgroundColor?: string }>(({ backgroundColor }) => ({
  position: 'absolute',
  height: '100%',
  backgroundColor,
  borderRadius: '20px',
  transition: 'left .5s',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

type LabelProps = { children?: ReactNode; style?: CSSProperties };

type SelectProps = {
  value?: string | number;
  label?: string;
  options: { label: string; value: string | number }[];
  attention?: boolean;
  style?: CSSProperties;
  itemWidth?: number;
  onChange: (value: string | number) => void;
};

type SelectBaseProps = SelectProps & {
  selectedColor?: string;
  Label?: FC<LabelProps>;
};

const ButtonSelectBase = ({
  value,
  options,
  onChange,
  style,
  itemWidth = 100,
  selectedColor,
  Label = LabelBase,
}: SelectBaseProps) => {
  const selectedIndex = value === undefined ? -1 : options.findIndex(({ value: optionValue }) => optionValue === value);
  const { label: selectedLabel } = options[selectedIndex] ?? {};

  return (
    <SelectContainer style={{ width: `${options.length * itemWidth}px`, ...style }}>
      {options.map(option => {
        const { label, value: optionValue } = option;
        const selected = optionValue === value;

        return (
          <SelectOption
            style={{ width: itemWidth, opacity: selected ? 0 : undefined }}
            key={optionValue}
            selected={selected}
            onClick={selected ? undefined : () => onChange?.(optionValue)}
          >
            <Label>{label}</Label>
          </SelectOption>
        );
      })}
      <Selection backgroundColor={selectedColor} style={{ left: selectedIndex * itemWidth, width: itemWidth }}>
        {selectedLabel && <Label style={{ color: 'white' }}>{selectedLabel}</Label>}
      </Selection>
    </SelectContainer>
  );
};

export const ButtonSelectNorlys = (props: SelectProps) => {
  return <ButtonSelectBase {...props} selectedColor={NorlysColor.secondary.purple} Label={LabelNorlys} />;
};

const LabelNorlys = ({ children, style }: LabelProps) => (
  <div style={{ ...NorlysFont.style('default', 14), ...style }}>{children}</div>
);

export const ButtonSelect = (props: SelectProps) => {
  return <ButtonSelectBase {...props} selectedColor={BodilColor.ui.action.default} />;
};

const LabelBase = ({ children, style }: LabelProps) => (
  <Heading level={3} style={style}>
    {children}
  </Heading>
);
